import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input} from '@angular/core';
import {catchError, EMPTY, Subscription, switchMap, tap, timeout} from 'rxjs';
import {HelpersService} from '../../../../../services/helpers.service';
import {ContractMemberModel, ContractStepModel} from '../../../../../models/contract.model';
import {ContractControlService} from '../../../../../services/contracts/contract-control.service';
import {AlertService} from '../../../../../services/notify/alert.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AppPinDialogComponentMT} from '../../../../../components/material/pin-dialog/app-pin-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {DialogsMTService} from '../../../../../services/modal/dialogs-mt.service';
import * as XLSX from 'xlsx'
import {TFormState} from '../../../../../models/forms.model';

type TStepSignatores = ContractMemberModel & { full_name?: string, sign_data?: IMembersSignedStep }

interface IMembersSignedStep {
    created_at: string,
    member_guid: string,
}

@Component({
    selector: 'app-credit-letter-sign',
    templateUrl: './credit-letter-sign.html',
    styleUrl: './credit-letter-sign.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppCreditLetterSignFormComponentMT {
    public CONTRACT_STATUSES_TO_SIGN = ['verified']
    public formState: TFormState = 'Default'
    @Input() stepToSign: ContractStepModel
    @Input() contractGuid: string
    @Input() contractStatus: string
    @Input() dialogWrapper:MatDialogRef<any>
    public signatories: TStepSignatores[] = []
    public stepIsSignedByMember: boolean
    public isMemberSigner: boolean
    public signFormError: string
    private subscriptions: Subscription = new Subscription()
    private pinDialogRef: MatDialogRef<AppPinDialogComponentMT>

    constructor(
        private helpersService: HelpersService,
        private contractControlService: ContractControlService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private dialogsMTService: DialogsMTService,
        private dialog: MatDialog,
        private changeDetectionRef: ChangeDetectorRef
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe()
    }

    ngOnInit(): void {
        this.setSignFormError()
        this.signatories = this.stepToSign.target?.payer_project?.members?.signatories as TStepSignatores[]
        this.stepToSign.target?.payer_private?.member ? this.signatories = [{
            member: this.stepToSign.target.payer_private.member,
            can_verify: true,
            verified_at: '',
        }] : null;
        if (this.signatories) {
            this.signatories.forEach(el => {
                el.full_name = this.helpersService.fullName(el.member)
                el.sign_data = this.stepToSign?.members_signed_step?.find(signer => signer.member_guid === el.member.guid)
            });
            this.sortSignoteries();
            if (this.stepToSign?.members_signed_step) {
                this.stepIsSignedByMember = this.stepToSign?.members_signed_step?.filter(el => el.member_guid === this.contractControlService.currentMember.guid)?.length > 0
            }
            this.isMemberSigner = this.signatories.filter(el => el.member.guid === this.contractControlService.currentMember.guid).length > 0
        }
    }

    signOnOffCreditLetter(isSign: boolean): void {
        let creditLetterGuid = this.stepToSign.target?.payer_project?.credit_letter_guid ||
            this.stepToSign.target?.payer_private?.credit_letter_guid || '';

        if (!creditLetterGuid && this.contractGuid) {
            return this.alertService.setAlert('Невозможно провести операцию. Отсутствует номер контракта или обеспечения', 'blue');
        }

        this.pinDialogRef = this.dialog.open(AppPinDialogComponentMT);
        this.pinDialogRef.componentInstance.isSign = isSign;
        this.pinDialogRef.componentInstance.header = this.translateService.instant(isSign ?
            'MODALS.HEADERS.SIGN_PAYMENT' : 'MODALS.HEADERS.RECALL_PAYMENT');

        this.subscriptions.add(this.pinDialogRef.afterClosed()
            .pipe(
                tap(() => {
                    this.formState = 'Submitting';
                    this.changeDetectionRef.detectChanges()
                }),
                switchMap((pin: { pin: string }) => {
                    if (pin.pin) {
                        return this.contractControlService.onSignOnOffCreditLetter(pin.pin, creditLetterGuid, isSign, this.contractGuid)
                    }
                    return null;
                }), catchError(() => {
                    this.formState = 'Default';
                    this.changeDetectionRef.detectChanges();
                    return EMPTY;
                })
            ).subscribe(data => {
                this.dialogWrapper ? this.dialogWrapper.close() : null;
                this.formState = 'Default';
                this.changeDetectionRef.detectChanges();
                this.alertService.setAlert(data.message, 'green');
            })
        );
    }

    setSignFormError() {
        switch (this.stepToSign.target?.target_status) {
            case 'NO_MONEY':
                this.signFormError = this.translateService.instant('CONTRACTS.ERRORS.NO_MONEY_SIGN_CL')
                break;
            case 'SERVICE_NOT_AVAILABLE':
                this.signFormError = this.translateService.instant('CONTRACTS.ERRORS.NOT_AVAILABLE')
                break;
        }
    }

    sortSignoteries() {
        let notSignedMembers = this.signatories.filter(el => !el.sign_data).sort((el, el1) => (el.full_name || '') > (el1.full_name || '') ? -1 : 1)
        let signedMembers = this.signatories.filter(el => el.sign_data).sort((el, el1) => (el.full_name || '') > (el1.full_name || '') ? -1 : 1)
        this.signatories = [...notSignedMembers, ...signedMembers]
    }

    sentNotification(reciver: TStepSignatores) {
        this.dialogsMTService.setNotification({
            actionHeader: this.translateService.instant('NOTIFY.REMINDER_SENT'),
            actionBody: this.translateService.instant('NOTIFY.REMINDER_SENT_BODY').replace('${1}', reciver.full_name).replace('${2}', reciver.member.email)
        })
    }

    saveToExcelSignatores() {
        let dataToExport = structuredClone(this.signatories);
        const fileName = 'подписанты' + ".xlsx";
        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Подписанты');
        XLSX.writeFile(wb, fileName);
    }
}
